import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WarehouseToken, EnumToken } from '@sleed/bizcommerce-api';
import { warehousesApi } from '~/lib/api/client';
import { formToToken, type WarehouseFormValues } from '~/lib/schemas/warehouse';

export const warehouseKeys = {
	all: ['warehouses'] as const,
	list: () => [...warehouseKeys.all, 'list'] as const,
	types: () => [...warehouseKeys.all, 'types'] as const
};

export function useWarehousesList() {
	return useQuery<WarehouseToken[]>({
		queryKey: warehouseKeys.list(),
		queryFn: async () => (await warehousesApi.getAll()).data.items ?? []
	});
}

/** Warehouse type lookup (Φυσική/Εικονική κ.λπ.) for the type column + edit dropdown. */
export function useWarehouseTypes() {
	return useQuery<EnumToken[]>({
		queryKey: warehouseKeys.types(),
		queryFn: async () => (await warehousesApi.getWarehouseTypes()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useWarehouseMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: warehouseKeys.list() });
	const create = useMutation({
		mutationFn: async (form: WarehouseFormValues) => (await warehousesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: WarehouseFormValues) => (await warehousesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await warehousesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
